/**
 * 크레딧 — 사업장 크레딧 지갑 서비스 (사장님 앱 전용)
 *
 *  BE 엔드포인트 (/business/credit/*) 와 1:1 매핑된 React Query 훅 모음.
 *  타입은 BE DTO(BusinessCreditDto.*) 와 키 이름까지 정합.
 *
 *  ⚠ 추후 "크레딧" 명칭이 변경되어도 (예: 멍콩/도토리) DB·API·코드는 그대로 유지.
 *     UI 라벨만 src/utils/credit.ts 한 곳에서 일괄 교체. (네이밍 추상화 방침)
 */
import { useQuery } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import type { BaseApiResponse } from "~/types/common";

/* ─────────────────────────────────────────────────────────────────
 *  타입 정의 — BE DTO 와 1:1 매핑
 * ───────────────────────────────────────────────────────────────── */

/** 트랜잭션 방향 */
export type CdCreditDirection = "CREDIT" | "DEBIT";

/**
 * 트랜잭션 목록 필터.
 *  - USAGE: 사용내역 — 차감 + 이용권 회수 환원(CREDIT 이지만 사용의 되돌림이라 함께 보여준다)
 *  - DEBIT/CREDIT: 방향 그대로
 */
export type CreditTxFilter = CdCreditDirection | "USAGE";

/** 트랜잭션 타입 — 지급/차감 카테고리 */
export type CdCreditTxType =
  // 지급
  | "GRANT_SIGNUP"
  | "GRANT_MONTHLY_FREE"
  | "GRANT_PROMOTION"
  | "GRANT_REFERRAL"
  | "PURCHASE"
  | "REFUND_PAYMENT_FEE"
  | "ADMIN_ADJUSTMENT"
  // 차감
  | "USE_AI_DIARY_TEXT"
  | "USE_AI_DIARY_PHOTO"
  | "USE_AI_DIARY_VIDEO"
  | "USE_AI_DIARY_REGEN"
  | "USE_TICKET_OFFLINE"
  | "USE_SMS"
  | "USE_KAKAO_ALIM"
  | "USE_CONTRACT_PUSH"
  | "USE_PHOTO_BACKUP"
  | "EXPIRE_FREE"
  | "REFUND_TO_USER"
  // 되돌림 — 이용권 회수 시 차감분 환원 (CREDIT 방향)
  | "REFUND_TICKET_REVOKE"
  | "REVERSAL";

/** 본인 회사 크레딧 잔액. */
export interface CompanyCreditWallet {
  companySeq: number;
  balance: number;
  freeBalance: number;
  paidBalance: number;
  lifetimeGranted: number;
  lifetimeUsed: number;
  dtFreeExpire?: string | null;
  cdPlan?: string;
  exemptUntil?: string | null;
}

/** 트랜잭션 1행 (사장님 표시용). */
export interface CreditTxItem {
  txSeq: number;
  companySeq: number;
  cdTxType: CdCreditTxType;
  cdDirection: CdCreditDirection;
  amount: number;
  balanceAfter: number;
  sourceType?: string;
  sourceSeq?: number;
  description: string;
  dtRegist: string;
}

/** 트랜잭션 페이지 응답. */
export interface CreditTxPage {
  content: CreditTxItem[];
  totalPages: number;
  totalElements: number;
  page: number;
  size: number;
}

/** 이번 달 사용 요약 (카테고리별). */
export interface CreditMonthlySummary {
  year: number;
  month: number;
  totalUsed: number;
  totalRefunded: number;
  byCategory: Array<{
    label: string;
    iconEmoji: string;
    count: number;
    amount: number;
    cdTxTypes: CdCreditTxType[];
  }>;
}

/** 사장님 노출용 충전 패키지. */
export interface CreditPackage {
  packageSeq: number;
  packageName: string;
  amount: number;
  priceKrw: number;
  discountRate?: number | null;
  popularYn?: boolean | null;
}

/** 액션 차감 비용 + 잔액 프리체크 응답. */
export interface ActionCostCheck {
  cdTxType: string;
  actionName?: string | null;
  qty: number;
  unitPrice: number;
  totalCost: number;
  currentBalance: number;
  sufficient: boolean;
  exempt: boolean;
}

/* ─────────────────────────────────────────────────────────────────
 *  HTTP 헬퍼 — BaseResponse 에서 data 추출
 * ───────────────────────────────────────────────────────────────── */

/** axios 응답의 BaseResponse 래퍼에서 data 만 안전하게 꺼냄. */
const unwrap = <T,>(payload: BaseApiResponse<T> | T | undefined): T => {
  if (payload == null) throw new Error("응답이 비어있습니다");
  if (typeof payload === "object" && payload !== null && "data" in (payload as any)) {
    return (payload as BaseApiResponse<T>).data;
  }
  return payload as T;
};

const E = API_ENDPOINTS.BUSINESS_CREDIT;

/* ─────────────────────────────────────────────────────────────────
 *  Fetcher
 * ───────────────────────────────────────────────────────────────── */

const fetchWallet = async (): Promise<CompanyCreditWallet> => {
  const { data } = await privateHttp.get<BaseApiResponse<CompanyCreditWallet>>(E.WALLET);
  return unwrap(data);
};

const fetchTxList = async (
  params: { page?: number; size?: number; direction?: CreditTxFilter } = {},
): Promise<CreditTxPage> => {
  const { data } = await privateHttp.get<BaseApiResponse<CreditTxPage>>(E.TRANSACTIONS, {
    params: {
      page: params.page ?? 0,
      size: params.size ?? 20,
      ...(params.direction ? { direction: params.direction } : {}),
    },
  });
  return unwrap(data);
};

const fetchMonthlySummary = async (): Promise<CreditMonthlySummary> => {
  const { data } = await privateHttp.get<BaseApiResponse<CreditMonthlySummary>>(E.MONTHLY_SUMMARY);
  return unwrap(data);
};

const fetchPackages = async (): Promise<CreditPackage[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<CreditPackage[]>>(E.PACKAGES);
  return unwrap(data) ?? [];
};

/* ─────────────────────────────────────────────────────────────────
 *  React Query 훅
 *   - 시그니처는 mock 시절과 동일 유지 — 컴포넌트 코드 변경 0
 * ───────────────────────────────────────────────────────────────── */

const KEY = {
  wallet:   ["business-credit", "wallet"]   as const,
  txList:   ["business-credit", "tx-list"]  as const,
  monthly:  ["business-credit", "monthly"]  as const,
  packages: ["business-credit", "packages"] as const,
};

/** 크레딧 잔액 조회 (헤더 칩 + 상세 페이지 공통). */
export const useCompanyCreditWallet = () =>
  useQuery({
    queryKey: KEY.wallet,
    queryFn: fetchWallet,
    staleTime: 30 * 1000,
  });

/** 트랜잭션 페이지 조회. direction=USAGE(차감+환원) / DEBIT(차감만) / CREDIT(적립). */
export const useCompanyCreditTxList = (
  params: { page?: number; size?: number; direction?: CreditTxFilter } = {},
) =>
  useQuery({
    queryKey: [...KEY.txList, params.page ?? 0, params.size ?? 20, params.direction ?? "ALL"],
    queryFn: () => fetchTxList(params),
    staleTime: 30 * 1000,
  });

/** 이번 달 카테고리별 사용 요약. */
export const useCompanyCreditMonthlySummary = () =>
  useQuery({
    queryKey: KEY.monthly,
    queryFn: fetchMonthlySummary,
    staleTime: 60 * 1000,
  });

/** 충전 패키지 목록. */
export const useCreditPackages = () =>
  useQuery({
    queryKey: KEY.packages,
    queryFn: fetchPackages,
    staleTime: 60 * 60 * 1000, // 1h
  });

/**
 * 액션 차감 비용 + 잔액 프리체크 (서버 호출).
 *  이용권 발급/AI 알림장 등 차감 액션 직전에 호출.
 *  sufficient=false 면 충전 안내 모달, true 면 진행 확인 모달 분기.
 *
 *  ⚠ USE_TICKET_OFFLINE(이용권 수동 발급)은 반드시 productTicketSeq 를 함께 전달.
 *    미전달 시 서버가 단가표 fallback 으로 계산해 실차감(KRW 환산)과 금액이 달라질 수 있음.
 */
export const checkActionCost = async (
  cdTxType: string,
  qty: number = 1,
  productTicketSeq?: number,
): Promise<ActionCostCheck> => {
  const { data } = await privateHttp.get<BaseApiResponse<ActionCostCheck>>(E.CHECK_ACTION, {
    params: {
      cdTxType,
      qty,
      ...(productTicketSeq != null ? { productTicketSeq } : {}),
    },
  });
  return unwrap(data);
};
